import { FC, FormEvent } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import Button from "@/components/Button";
import { FormLayout, Larger } from "@/components/Forms";
import FileField from "@/components/Forms/FileField";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { RemoveButton } from "@/components/Testing";
import useAlerts from "@/hooks/useAlerts";
import { MediaResource } from "@/types/resources";

type FormDataT = {
  variations: {
    image: File | null;
  }[];
};

interface Props {
  onClose: () => void;
  type: "Foregrounds" | "Backgrounds";
}

const RemoveButtonHolder = styled.div`
  margin-bottom: 8px;
  text-align: right;
`;

const ThumbnailsUploadForm: FC<Props> = ({ onClose, type }) => {
  const { teamId } = useParams();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const methods = useForm<FormDataT>({
    defaultValues: {
      variations: [{ image: null }],
    },
  });

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "variations",
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ variations }) => {
      try {
        const promises = variations.map((variation) => {
          const formData = new FormData();

          if (variation.image) {
            formData.append("image", variation.image);
          }

          formData.append(
            "collectionName",
            type === "Foregrounds" ? "thumb_foregrounds" : "thumb_backgrounds"
          );

          return api.post<typeof formData, MediaResource>(
            `/v1/teams/${teamId}/media`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
        });

        const images = await Promise.all(promises);

        if (!images) return;

        alerts.success(
          type === "Foregrounds"
            ? "Foregrounds uploaded successfully"
            : "Backgrounds uploaded successfully"
        );

        if (type === "Foregrounds") {
          queryClient.refetchQueries(["generator_foregrounds", { teamId }], {
            exact: true,
          });
        } else {
          queryClient.refetchQueries(["generator_backgrounds", { teamId }], {
            exact: true,
          });
        }

        onClose();
      } catch (error) {
        handleFormErrors<FormDataT>(error, setError);
      }
    })(e);
  };

  const onFilesAdded = (files?: FileList | null) => {
    if (!files?.length) {
      return;
    }

    Array.from(files!).forEach((file, index) => {
      if (index === 0) {
        return;
      }

      if (!["image/png", "image/jpg", "image/jpeg"].includes(file.type)) {
        return;
      }

      append({
        image: file,
      });
    });
  };

  return (
    <Modal>
      <SidePanel heading={`Upload ${type}`} onClose={onClose}>
        <FormProvider {...methods}>
          <SidePanelBody onSubmit={onSubmit} autoComplete="off">
            <SidePanelContent>
              <FormLayout>
                {fields.map((_, index) => (
                  <Larger key={index}>
                    {index > 0 && (
                      <RemoveButtonHolder>
                        <RemoveButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(index);
                          }}
                        >
                          Remove
                        </RemoveButton>
                      </RemoveButtonHolder>
                    )}
                    <FileField
                      name={`variations.${index}.image`}
                      fileType="image"
                      multiple
                      notes="JPG, PNG. Size: 1920 x 1080 / Up to 2MB</br>Upload multiple files"
                      onFilesAdded={onFilesAdded}
                    />
                    {!!errors.variations?.[index]?.image && (
                      <ValidationMessage>
                        {errors.variations?.[index]?.image?.message}
                      </ValidationMessage>
                    )}
                  </Larger>
                ))}
              </FormLayout>
            </SidePanelContent>
            <SidePanelFooter $actions>
              <Button disabled={isSubmitting} loading={isSubmitting}>
                Upload
              </Button>
              <Button color="secondary" onClick={onClose} type="button">
                Cancel
              </Button>
            </SidePanelFooter>
          </SidePanelBody>
        </FormProvider>
      </SidePanel>
    </Modal>
  );
};

export default ThumbnailsUploadForm;
